import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AUTOMATION_LIMITS,
  AUTOMATION_TEMPLATES,
  AUTOMATIONS,
  AUTOMATIONS_DEFAULTS,
  TRACKING_DEFAULTS,
  type AutomationKey,
  type AutomationsSetting,
  type AutomationStatsDto,
  type TrackingSetting,
} from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { TextField, ToggleField } from '@/components/fields';
import { Page, SaveBar } from '@/components/page';
import { Alert, Button, Card, Field, Pill, Skeleton, Switch } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { count, money, share } from '@/lib/format';
import { couponOptions } from '@/lib/options';
import { useSettings } from '@/pages/settings/schema';
import { settingValue } from '@/stores/settings.store';

const AUTOMATIONS_KEY = 'marketing.automations';
const TRACKING_KEY = 'marketing.tracking';
const STATS_DAYS = 30;

/** The setting as the form holds it: every field present, whatever an older store saved. */
function automationsOf(value: unknown): AutomationsSetting {
  const stored = (value ?? {}) as Partial<AutomationsSetting>;
  return {
    reviewRequest: { ...AUTOMATIONS_DEFAULTS.reviewRequest, ...stored.reviewRequest },
    winBack: { ...AUTOMATIONS_DEFAULTS.winBack, ...stored.winBack },
    repurchase: { ...AUTOMATIONS_DEFAULTS.repurchase, ...stored.repurchase },
    welcome: { ...AUTOMATIONS_DEFAULTS.welcome, ...stored.welcome },
    birthday: { ...AUTOMATIONS_DEFAULTS.birthday, ...stored.birthday },
  };
}

function wholeOr(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Marketing → Automations (ADR-099): the mails that send themselves — each
 * switched on here, worded in Settings → Notifications — with what each did
 * over the last thirty days, and whether opens and clicks are counted.
 */
export function AutomationsPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { entries, loading, saving, error, save } = useSettings();
  const storedAutomations = useMemo(() => automationsOf(settingValue(entries, AUTOMATIONS_KEY)), [entries]);
  const storedTracking = useMemo<TrackingSetting>(() => ({ ...TRACKING_DEFAULTS, ...(settingValue(entries, TRACKING_KEY) as Partial<TrackingSetting> | undefined) }), [entries]);
  const [draft, setDraft] = useState(storedAutomations);
  const [tracking, setTracking] = useState(storedTracking);
  const [stats, setStats] = useState<AutomationStatsDto[] | null>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => setDraft(storedAutomations), [storedAutomations]);
  useEffect(() => setTracking(storedTracking), [storedTracking]);
  useEffect(() => {
    withSession(() => adminApi.automations.stats(STATS_DAYS))
      .then((page) => setStats(page.items))
      .catch((caught: unknown) => setFailure(errorMessage(caught, 'Could not load the numbers.')));
  }, []);

  if (loading && entries.length === 0) return <Skeleton className="m-8 h-96" />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(storedAutomations) || JSON.stringify(tracking) !== JSON.stringify(storedTracking);
  const statsOf = (key: AutomationKey) => stats?.find((entry) => entry.key === key)?.stats;

  async function submit() {
    const ok = JSON.stringify(draft) === JSON.stringify(storedAutomations) || (await save(AUTOMATIONS_KEY, draft));
    if (ok && JSON.stringify(tracking) !== JSON.stringify(storedTracking)) await save(TRACKING_KEY, tracking);
  }

  async function runNow() {
    setRunning(true);
    setNotice(null);
    try {
      const sent = await withSession(() => adminApi.automations.run());
      setNotice(t('automationsRan', { count: count(sent.reviewRequest + sent.winBack + sent.repurchase + sent.birthday, locale) }));
    } catch (caught) {
      setFailure(errorMessage(caught, 'Could not run the automations.'));
    } finally {
      setRunning(false);
    }
  }

  const set = <K extends keyof AutomationsSetting>(key: K, patch: Partial<AutomationsSetting[K]>) =>
    setDraft((current) => ({ ...current, [key]: { ...current[key], ...patch } }));

  const card = (key: AutomationKey, enabled: boolean | null, onToggle: ((next: boolean) => void) | null, body: React.ReactNode) => {
    const numbers = statsOf(key);
    return (
      <Card key={key} className="flex flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">{t(`automation_${key}`)}</h2>
            <p className="mt-0.5 text-sm text-ink-muted">{t(`automationHint_${key}`)}</p>
          </div>
          {onToggle && enabled !== null ? <Switch checked={enabled} onChange={onToggle} label={t(`automation_${key}`)} /> : <Pill tone="info">{t('automationInIntegrations')}</Pill>}
        </div>
        {body}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3 text-xs text-ink-muted">
          <span>
            {numbers
              ? t('automationNumbers', {
                  sent: count(numbers.sent, locale),
                  opened: share(numbers.opened, numbers.sent, locale),
                  clicked: share(numbers.clicked, numbers.sent, locale),
                  orders: count(numbers.orders, locale),
                  revenue: money(numbers.revenue, locale),
                })
              : '…'}
          </span>
          <Link to={`/settings/notifications?key=${encodeURIComponent(AUTOMATION_TEMPLATES[key])}`} className="font-medium text-brand-700 hover:underline">
            {t('automationWording')}
          </Link>
        </div>
      </Card>
    );
  };

  return (
    <Page
      title={t('automationsPage')}
      subtitle={t('automationsPageHint')}
      actions={
        <Button variant="secondary" disabled={running} onClick={() => void runNow()}>
          {running ? t('loading') : t('automationsRunNow')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {error || failure ? <Alert>{error ?? failure}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}
        <p className="text-xs text-ink-muted">{t('automationsStatsNote', { days: count(STATS_DAYS, locale) })}</p>
        <div className="grid gap-4 xl:grid-cols-2">
          {AUTOMATIONS.map((key) => {
            if (key === 'review_request') {
              return card(key, draft.reviewRequest.enabled, (enabled) => set('reviewRequest', { enabled }), (
                <TextField
                  label={t('automationDaysAfterDelivery')}
                  type="number"
                  inputMode="numeric"
                  latin
                  value={String(draft.reviewRequest.days)}
                  onChange={(value) => set('reviewRequest', { days: wholeOr(value, draft.reviewRequest.days) })}
                />
              ));
            }
            if (key === 'win_back') {
              return card(key, draft.winBack.enabled, (enabled) => set('winBack', { enabled }), (
                <div className="grid gap-3 sm:grid-cols-3">
                  <TextField
                    label={t('automationDaysSinceOrder')}
                    hint={t('automationDaysSinceOrderHint')}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={String(draft.winBack.days)}
                    onChange={(value) => set('winBack', { days: wholeOr(value, draft.winBack.days) })}
                  />
                  <TextField
                    label={t('automationCodePercent')}
                    hint={t('automationCodePercentHint', { max: String(AUTOMATION_LIMITS.discountPercent) })}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={draft.winBack.discountPercent ? String(draft.winBack.discountPercent) : ''}
                    onChange={(value) => set('winBack', { discountPercent: value.trim() ? wholeOr(value, 0) || null : null })}
                  />
                  <TextField
                    label={t('automationCodeHours')}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={String(draft.winBack.discountHours)}
                    onChange={(value) => set('winBack', { discountHours: wholeOr(value, draft.winBack.discountHours) })}
                  />
                </div>
              ));
            }
            if (key === 'birthday') {
              // On a customer's birthday (ADR-115): a code for them alone and bonus points, both optional.
              return card(key, draft.birthday.enabled, (enabled) => set('birthday', { enabled }), (
                <div className="grid gap-3 sm:grid-cols-3">
                  <TextField
                    label={t('automationCodePercent')}
                    hint={t('automationCodePercentHint', { max: String(AUTOMATION_LIMITS.discountPercent) })}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={draft.birthday.discountPercent ? String(draft.birthday.discountPercent) : ''}
                    onChange={(value) => set('birthday', { discountPercent: value.trim() ? wholeOr(value, 0) || null : null })}
                  />
                  <TextField
                    label={t('automationCodeHours')}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={String(draft.birthday.discountHours)}
                    onChange={(value) => set('birthday', { discountHours: wholeOr(value, draft.birthday.discountHours) })}
                  />
                  <TextField
                    label={t('automationBirthdayPoints')}
                    hint={t('automationBirthdayPointsHint')}
                    type="number"
                    inputMode="numeric"
                    latin
                    value={draft.birthday.points ? String(draft.birthday.points) : ''}
                    onChange={(value) => set('birthday', { points: value.trim() ? wholeOr(value, 0) : 0 })}
                  />
                </div>
              ));
            }
            if (key === 'repurchase') {
              return card(key, draft.repurchase.enabled, (enabled) => set('repurchase', { enabled }), <p className="text-sm text-ink-muted">{t('automationRepurchaseHow')}</p>);
            }
            if (key === 'welcome') {
              return card(key, draft.welcome.enabled, (enabled) => set('welcome', { enabled }), (
                <Field label={t('automationWelcomeCoupon')} htmlFor="welcome-coupon" hint={t('automationWelcomeCouponHint')}>
                  <AsyncSelect
                    id="welcome-coupon"
                    value={draft.welcome.discountId ?? ''}
                    emptyLabel={t('popupNoCoupon')}
                    fetchPage={couponOptions.page}
                    fetchOne={couponOptions.one}
                    onChange={(discountId) => set('welcome', { discountId: discountId || null })}
                  />
                </Field>
              ));
            }
            return card(key, null, null, (
              <p className="text-sm text-ink-muted">
                {t('automationCartSecondHow')}{' '}
                <Link to="/settings/integrations" className="font-medium text-brand-700 hover:underline">
                  {t('automationOpenIntegrations')}
                </Link>
              </p>
            ));
          })}
        </div>

        <Card className="flex flex-col gap-3 p-5">
          <h2 className="text-base font-semibold text-ink">{t('trackingTitle')}</h2>
          <p className="text-sm text-ink-muted">{t('trackingHint')}</p>
          <ToggleField label={t('trackingOpens')} hint={t('trackingOpensHint')} checked={tracking.opens} onChange={(opens) => setTracking((current) => ({ ...current, opens }))} />
          <ToggleField label={t('trackingClicks')} hint={t('trackingClicksHint')} checked={tracking.clicks} onChange={(clicks) => setTracking((current) => ({ ...current, clicks }))} />
        </Card>

        <SaveBar
          saving={saving === AUTOMATIONS_KEY || saving === TRACKING_KEY}
          dirty={dirty}
          onSave={() => void submit()}
          onDiscard={() => {
            setDraft(storedAutomations);
            setTracking(storedTracking);
          }}
        />
      </div>
    </Page>
  );
}
